import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { isMobile, isDesktop } from '../../utils/devices';
import { CSSTransition } from 'react-transition-group';
import { logoutAsync } from 'src/features/auth';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';

import { MessagesIcon } from '@mavenmachines/maven-messages';

import ArrowIcon from 'src/assets/layout/header/arrow.svg';
import LogoIcon from 'src/assets/layout/header/mavenLogo.svg';

import { Navigation } from 'src/features/layout/Navigation';
import { Modal } from 'src/components/Modal/Modal';
import { DutyStatusForm } from 'src/components/DutyStatusForm';
import {
  DUTY_STATUS,
  ELD_DRIVER_STATUS,
  ELD_DRIVER_STATUS_REVERSE,
  ELD_DRIVER_STATUS_TEXT,
} from 'src/constants/ELDConstants';

import './header.scss';
import {
  fetchDriverInfo,
  fetchDutyStatusLogs,
  changeDutyStatus,
  setDutyStatusSuccess,
  fetchDriverConfig,
} from 'src/features/profile/store/profileStore';
import {
  getDriverConfig,
  getDriverInfo,
  getDriverPermissions,
  getDutyStatus,
  getPatchDutyStatusSuccess,
  getUserId,
} from 'src/features/profile/store';
import {
  fetchCurrentDayHosLog,
  fetchHosLog,
  setEditingStatus,
  setInsertingDutyStatus,
  setIsAddingException,
  setUnsavedHOSExceptionsEditsPresent,
  setUnsavedHOSLogEditsPresent,
} from 'src/features/hosLogs/store/hosLogStore';
import {
  getHosLogs,
  getUnsavedHOSExceptionsEditsPresent,
  getUnsavedHOSLogEditsPresent,
} from 'src/features/hosLogs/store';

import { Link } from 'react-router-dom';
import { PERMISSIONS } from '../../constants/permissionsConstants';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import HosLogEditModal from 'src/components/HOSLogEditModal/HosLogEditModal';
import { getEmbeddedState } from './Navigation/store';
import { toastr } from 'react-redux-toastr';

interface Props {
  isAuthenticated?: boolean;
}

export const Header: React.FC<Props> = () => {
  const [showNavigation, setShowNavigation] = useState(!isMobile);
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);
  const [showDutyStatusDropDown, setShowDutyStatusDropDown] = useState(false);
  const [showDutyStatusModal, setShowDutyStatusModal] = useState(false);
  const [nextDutyStatus, setNextDutyStatus] = useState(0);
  const [coords, setCoords] = useState({ latitude: -1, longitude: -1 });
  const [noDutyStatus, setNoDutyStatus] = useState(false);

  const history = useHistory();

  const nodeRefLogout = useRef(null);
  const nodeRefDutyStatus = useRef(null);
  const nodeRefNavigation = useRef(null);
  const logoutRef = useRef<HTMLDivElement>(null);
  const dutyStatusRef = useRef<HTMLDivElement>(null);

  const driverInfo = useSelector(getDriverInfo);
  const driverPermissions = useSelector(getDriverPermissions);
  const driverConfig = useSelector(getDriverConfig);
  const dispatch = useDispatch();
  const hosLogs = useSelector(getHosLogs);

  const lastLogDateTime = useMemo(() => {
    // Get the last calendar log (index 0)
    const hosGridObject = hosLogs[0]?.hosGridObject;
    const time = hosGridObject && hosGridObject[hosGridObject.length - 1].timeISO;
    return time;
  }, [hosLogs]);

  const location = useLocation();

  const isEmbedded = useSelector(getEmbeddedState);

  const unsavedHOSLogDutyChangesPresent = useSelector(getUnsavedHOSLogEditsPresent);

  const unsavedHOSExceptionChangesPresent = useSelector(getUnsavedHOSExceptionsEditsPresent);

  const [showLogEditModal, setShowLogEditModal] = useState(false);

  const discardDutyEdits = () => {
    dispatch(setEditingStatus(undefined));
    dispatch(setInsertingDutyStatus(false));
    dispatch(setUnsavedHOSLogEditsPresent(false));
  };

  const discardExceptionEdits = () => {
    dispatch(setUnsavedHOSExceptionsEditsPresent(false));
    dispatch(setIsAddingException(false));
  };

  const discardAllHosLogEdits = () => {
    discardDutyEdits();
    discardExceptionEdits();
    hideModal();
    dispatch(logoutAsync());
  };

  const hideModal = () => {
    setShowLogEditModal(false);
  };

  const toggleNavigationMenu = useCallback(isMobile => {
    if (isMobile) {
      setShowNavigation(prev => !prev);
    }
  }, []);

  const toggleLogoutMenu = useCallback(() => {
    setShowLogoutMenu(prev => !prev);
  }, []);

  const onLogoutHandler = () => {
    if (unsavedHOSExceptionChangesPresent) {
      setShowLogEditModal(true);
      return;
    }
    if (unsavedHOSLogDutyChangesPresent) {
      setShowLogEditModal(true);
      return;
    }
    dispatch(logoutAsync());
    setShowLogoutMenu(false);
  };

  const clickOutsideDropMenu = useCallback(e => {
    !(logoutRef.current && logoutRef.current.contains(e.target)) && setShowLogoutMenu(false);
    !(dutyStatusRef.current && dutyStatusRef.current.contains(e.target)) &&
      setShowDutyStatusDropDown(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', clickOutsideDropMenu);
    return () => {
      document.removeEventListener('mousedown', clickOutsideDropMenu);
    };
  }, [clickOutsideDropMenu]);

  const userId = useSelector(getUserId);
  const dutyStatus = useSelector(getDutyStatus);
  const patchDutyStatusSuccess = useSelector(getPatchDutyStatusSuccess);

  const getCurrentPosition = (): Promise<{ coords: { longitude: number; latitude: number } }> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  const fetchStatus = (userId: number) => {
    if (userId) {
      dispatch(
        fetchDutyStatusLogs({
          userId,
        }),
      );
    }
  };

  useEffect(() => {
    fetchStatus(userId);
  }, [userId]);

  useEffect(() => {
    dispatch(fetchDriverInfo());
  }, [dispatch]);

  useEffect(() => {
    if (driverInfo.employeeId !== 0) dispatch(fetchDriverConfig(driverInfo.employeeId));
  }, [dispatch, driverInfo]);

  // Initialize feature flag client
  const featureFlagClient = useLDClient();
  // Identify new user to LD only if driverInfo changes
  useEffect(() => {
    featureFlagClient?.identify(createLDUser());
  }, [driverInfo]);

  const createLDUser = () => {
    return {
      key: `employeeId=${driverInfo.employeeId}`,
      custom: {
        companyId: driverInfo.companyId,
      },
    };
  };

  const toggleDutyStatusMenu = useCallback(() => {
    setShowDutyStatusDropDown(prev => !prev);
  }, []);

  const todayDate = format(new Date(), 'yyyy-MM-dd');

  const openDutyStatusModal = useCallback(() => {
    setShowDutyStatusDropDown(false);
    setShowDutyStatusModal(true);
  }, [dispatch, userId, todayDate]);

  const closeDutyStatusModal = useCallback(() => {
    setShowDutyStatusModal(false);
    dispatch(setDutyStatusSuccess(false));
    setNextDutyStatus(0);
  }, [dispatch]);

  const onSubmitDutyStatusForm = useCallback(
    formData => {
      const dispatchData = {
        employeeId: userId,
        dutyStatusName: Object.values(DUTY_STATUS)[nextDutyStatus],
        enableReverseGeocode: false,
        ...formData,
      };
      if (nextDutyStatus === 0) {
        setNoDutyStatus(true);
        return;
      }
      dispatch(changeDutyStatus(dispatchData));
    },
    [nextDutyStatus, userId, dispatch],
  );

  const openUpdatedHosLogHandler = useCallback(() => {
    closeDutyStatusModal();
    const RELOAD_DELAY = 5000;
    toastr.success('Loading', 'Loading the new logs', { timeOut: RELOAD_DELAY });
    history.push('/');
    setTimeout(() => {
      window.location.reload();
    }, RELOAD_DELAY);
  }, [closeDutyStatusModal, history, todayDate]);

  const handleStatusButtonClicked = useCallback(
    status => {
      setNextDutyStatus(status);
      setNoDutyStatus(false);
    },
    [nextDutyStatus],
  );

  useEffect(() => {
    if (location.pathname === `/hos-log/${todayDate}` && patchDutyStatusSuccess) {
      dispatch(
        fetchHosLog({
          logId: `${userId}:${todayDate}`,
        }),
      );
    }
  }, [patchDutyStatusSuccess, location, todayDate, userId, dispatch]);

  useEffect(() => {
    getCurrentPosition().then(pos => setCoords(pos.coords));
  }, []);

  const renderMessagesIcon = () => {
    return driverPermissions.includes(PERMISSIONS.APP_MESSAGES_ACCESS) ? (
      <Link className="messages-link" to={'/messages'}>
        <MessagesIcon />
      </Link>
    ) : null;
  };

  const renderDutyStatus = () => {
    if (dutyStatus && dutyStatus.dutyStatusCode) {
      const status = ELD_DRIVER_STATUS_REVERSE[dutyStatus.dutyStatusCode];
      return (
        <>
          <div className="vertical-line"></div>
          <div className="header-status__wrapper" ref={dutyStatusRef}>
            <div className="header-status__title">Duty status</div>
            <div className="header-status" onClick={toggleDutyStatusMenu}>
              <span className={`header-status__${status}`}>{status}</span>
              <div className="arrow-icon">
                <img src={ArrowIcon} alt="ArrowIcon" />
              </div>
            </div>
            <CSSTransition
              in={showDutyStatusDropDown}
              timeout={400}
              classNames="drop-down-transition"
              unmountOnExit
              nodeRef={nodeRefDutyStatus}
            >
              <div className="header-status__dropdown" ref={nodeRefDutyStatus}>
                <ul>
                  <li onClick={openDutyStatusModal}>Change Status</li>
                </ul>
              </div>
            </CSSTransition>
          </div>
        </>
      );
    }
  };

  return (
    <>
      {showLogEditModal && (
        <HosLogEditModal
          title="Leave Page"
          message="All changes will be lost. Do you want to proceed?"
          proceedButtonText="Discard Changes"
          proceedButtonFn={() => {
            discardAllHosLogEdits();
          }}
          undoButtonText="Cancel"
          undoButtonFn={hideModal}
        />
      )}
      <div className={classNames('header', { 'is-desktop': !isMobile })}>
        {isMobile && (
          <div
            className={classNames('burger-icon', { 'is-active': showNavigation })}
            onClick={toggleNavigationMenu}
          >
            <span></span>
          </div>
        )}
        <img className="logo-icon" src={LogoIcon} alt="LogoIcon" />
        {isDesktop && renderMessagesIcon()}
        <div className="vertical-line"></div>
        <div className="user-name" ref={logoutRef}>
          <div className="user-name__box" onClick={toggleLogoutMenu}>
            {`${driverInfo.firstName} ${driverInfo.lastName}`}
            {!isEmbedded && (
              <div className="arrow-icon">
                <img src={ArrowIcon} alt="ArrowIcon" />
              </div>
            )}
          </div>
          {!isEmbedded && (
            <CSSTransition
              in={showLogoutMenu}
              timeout={400}
              classNames="drop-down-transition"
              unmountOnExit
              nodeRef={nodeRefLogout}
            >
              <div className="user-name__logout" ref={nodeRefLogout} onClick={onLogoutHandler}>
                Log Out
              </div>
            </CSSTransition>
          )}
        </div>
        {renderDutyStatus()}
      </div>

      <CSSTransition
        in={showNavigation}
        timeout={400}
        classNames="navigation-transition"
        unmountOnExit
        nodeRef={nodeRefNavigation}
      >
        <div
          className={classNames('navigation-transition__wrapper', { 'is-desktop': !isMobile })}
          ref={nodeRefNavigation}
        >
          <Navigation onClick={() => toggleNavigationMenu(isMobile)} />
        </div>
      </CSSTransition>

      {showDutyStatusModal && !isEmpty(driverConfig) && (
        <Modal
          header={'Change Current Duty Status'}
          confirmButton={'View Updated Log'}
          description={format(new Date(), 'EEE, MMM dd, yyyy')}
          onConfirmFunction={openUpdatedHosLogHandler}
          onCloseDialog={closeDutyStatusModal}
          className="modal-backdrop__duty-modal"
          showConfirmButton={patchDutyStatusSuccess}
        >
          <div className={patchDutyStatusSuccess ? 'utility_no_pointer_event' : ''}>
            <div className="modal-duty-status__info-box">
              {[ELD_DRIVER_STATUS.OFF, ELD_DRIVER_STATUS.SB, ELD_DRIVER_STATUS.ON].map(status => (
                <div
                  className="modal-duty-status__button"
                  onClick={() => handleStatusButtonClicked(status)}
                  key={status}
                >
                  <div
                    className={`modal-duty-status__button-${
                      noDutyStatus
                        ? 'error'
                        : status === nextDutyStatus
                        ? ELD_DRIVER_STATUS[nextDutyStatus].toLowerCase()
                        : 'empty'
                    }`}
                  >
                    {ELD_DRIVER_STATUS_REVERSE[status]}
                  </div>
                  {dutyStatus && <span>{ELD_DRIVER_STATUS_TEXT[status]}</span>}
                </div>
              ))}
            </div>
          </div>
          {!patchDutyStatusSuccess && (
            <DutyStatusForm
              onSubmit={onSubmitDutyStatusForm}
              timezone={driverConfig.DHTTimezone}
              lastLogDateTime={lastLogDateTime}
              coordinates={coords}
            />
          )}
        </Modal>
      )}
    </>
  );
};
